import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '../../../search-trip/helpers'
import PropTypes from 'prop-types'

import Alert from '../../../search-trip/components/Alert'
import Loader from '../../../search-trip/components/Loader'
import Button from '../../../search-trip/components/Button'
import DestinationsApartmentList from '../DestinationsApartmentList'
import { setFormValuesByParams, resetDestinations, resetParticipants } from '../../actions/apartmentActions'
import { fetchPossibleData } from '../../api/fetchPossibleData'
import { setCitiesList, setPopularCitiesList } from '../../actions/formActions'
import destinationIcon from '@assets/img/search-engine/destination-icon.svg';
import calendarIcon from '@assets/img/search-engine/calendar-icon.svg';
import participantIcon from '@assets/img/search-engine/participants-icon.svg';
import "./style.scss"
import { ADULTS_DEFAULT, CHILDREN_DEFAULT } from '../../consts/data'
import { CustomField, CalendarField } from '../../../search-trip/components/CustomField'
import DateRangePicker from '../DateRangePicker'
import Participants from '../Participants'

const ApartmentSearchForm = ({
    accommodationTypes = {},
    customDestination,
    apartmentState,
    setFormValuesByParams,
    setPopularCitiesList,
    setCitiesList,
    resetDestinations,
    resetParticipants,
    facilities,
    feeding,
    operator,
    hotelAttributes,
    activityAttributes,
    sort,
    standard,
    minDays,
    maxDays,
    priceMin,
    priceMax,
    priceFor,
}) => {
    const { t, i18n } = useTranslation()
    const [isLoading, setIsLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState(null)
    const getInitialPlaceholder = () => {
        if (apartmentState.hotel !== null) {
            return apartmentState.hotel
        }
        if (apartmentState.kierunek !== null) {
            return apartmentState.kierunek
        }
        return t('form.destinations.field_placeholder')
    }
    const [destinationPlaceholder, setDestinationPlaceholder] = useState(getInitialPlaceholder())
    const [participantsPlaceholder, setParticipantsPlaceholder] = useState(null)
    const [isValidParticipants, setIsValidParticipants] = useState(true)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [urlParamsApplied, setUrlParamsApplied] = useState(false)
    const honeypotRef = useRef(null)

    const [selectedTypes, setSelectedTypes] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const allowedTypes = Object.keys(accommodationTypes);

        const values = Array.from(params.entries())
            .filter(([key]) => /^typ\[\d*\]$/.test(key))
            .map(([, value]) => value.trim().toLowerCase())
            .filter(value => allowedTypes.includes(value));

        return Array.from(new Set(values));
    });

    const hasAccommodationCheckboxes = Object.keys(accommodationTypes).length > 0;

    const hasSelectedType = hasAccommodationCheckboxes
        ? selectedTypes.length > 0
        : Array.from(
            new URLSearchParams(window.location.search).entries()
        ).some(([key, value]) => (
            /^typ\[\d*\]$/.test(key) &&
            ['dom', 'apartament', 'pokoj', 'glamping'].includes(value)
        ));

    const toggleAccommodationType = (type) => {
        setSelectedTypes(current => (
            current.includes(type)
                ? current.filter(value => value !== type)
                : [...current, type]
        ));
    };

    const isDestinationMissing = (
        apartmentState.kierunek === null &&
        apartmentState.hotel === null &&
        !hasSelectedType
    );

    useEffect(() => {
        const currentLang = getCurrentLanguage()

        i18n.changeLanguage(currentLang).then(r => null)
    }, [i18n])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (
            customDestination &&
            !params.has('kierunek') &&
            !params.get('hotel')
        ) {
            params.set('kierunek', customDestination);
        }

        if (params.size > 0) {
            let hasApartmentParams = false;

            const allowedKeys = [
                'kierunek',
                'od',
                'do',
                'dorosli',
                'dzieci',
                'hotel',
                'filtry'
            ];

            Array.from(params.entries()).forEach(([key, value]) => {
                if (
                    allowedKeys.includes(key) &&
                    value !== null &&
                    value !== ''
                ) {
                    setFormValuesByParams(key, value);
                    hasApartmentParams = true;
                }
            });

            if (hasApartmentParams) {
                setUrlParamsApplied(true);
            }
        }
    }, [customDestination, setFormValuesByParams]);

    useEffect(() => {
        fetchPossibleData()
            .then(data => {
                setPopularCitiesList(data.popular)
                setCitiesList(data.cities)
            })
            .catch((err) => {
                setErrorMessage(err.message)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    useEffect(() => {
        let newPlaceholder = t('form.destinations.field_placeholder')

        if (apartmentState.kierunek !== null) {
            newPlaceholder = apartmentState.kierunek
        }

        if (apartmentState.hotel !== null) {
            newPlaceholder = apartmentState.hotel
        }

        setDestinationPlaceholder(newPlaceholder)
    }, [apartmentState.kierunek, apartmentState.hotel])

    useEffect(() => {
        const defaultPlaceholder = t('form.participants.default_placeholder')
        const { dorosli, dzieci } = apartmentState
        let placeholder = null;

        if (dorosli === ADULTS_DEFAULT && dzieci === CHILDREN_DEFAULT) {
            placeholder = defaultPlaceholder
        } else {
            placeholder = dzieci && dzieci.length > 0 ?
                `${dorosli}+${dzieci.length}` :
                `${dorosli} ${t('form.participants.adult')}`
        }

        setParticipantsPlaceholder(placeholder)
    }, [apartmentState.dorosli, apartmentState.dzieci])

    const resetParticipantsHandler = () => {
        resetParticipants();
    }

    const submitHandler = (e) => {
        e.preventDefault();

        if (honeypotRef.current && honeypotRef.current.value.trim() !== '') {
            return false;
        }

        setSubmitAttempted(true);

        if (isDestinationMissing) {
            return false;
        }
    
        const params = new URLSearchParams(window.location.search);

        if (hasAccommodationCheckboxes) {
            Array.from(params.keys()).forEach((key) => {
                if (key === 'typ' || /^typ\[\d*\]$/.test(key)) {
                    params.delete(key);
                }
            });

            selectedTypes.forEach((type) => {
                params.append('typ[]', type);
            });
        }

        [
            'kierunek',
            'hotel',
            'od',
            'do',
            'dorosli',
            'dzieci',
            'filtry',
            'page',
            'strona',
        ].forEach((key) => {
            params.delete(key);
        });

        const arrayParams = [
            { key: 'facilities', param: 'udogodnienia[]', value: facilities },
            { key: 'feeding', param: 'wyzywienie[]', value: feeding },
            { key: 'operator', param: 'operator[]', value: operator },
            { key: 'hotelAttributes', param: 'attributesHotel[]', value: hotelAttributes },
            { key: 'activityAttributes', param: 'attributesActivities[]', value: activityAttributes }
        ];
        
        const valueParams = [
            { key: 'standard', param: 'standard', value: standard },
            { key: 'minDays', param: 'min-dni', value: minDays },
            { key: 'maxDays', param: 'max-dni', value: maxDays },
            { key: 'priceMin', param: 'cena-min', value: priceMin },
            { key: 'priceMax', param: 'cena-max', value: priceMax },
            { key: 'priceFor', param: 'cena-laczna', value: priceFor },
            { key: 'sort', param: 'sort', value: sort }
        ];
    
        arrayParams.forEach(({ param, value }) => {
            if (value) {
                params.delete(param);

                try {
                    const parsed = JSON.parse(value);

                    if (Array.isArray(parsed)) {
                        parsed.forEach(el => params.append(param, el));
                    }
                } catch (err) {
                    params.append(param, value);
                }
            }
        });
    
        valueParams.forEach(({ param, value }) => {
            if (value) {
                try {
                    params.set(param, JSON.parse(value));
                } catch (err) {
                    params.set(param, value);
                }
            }
        });
    

    
        Object.entries(apartmentState)
            .filter(([key, value]) => {
                if (key === 'kierunek') return false;
                if (Array.isArray(value)) return value.length > 0;
                return value !== '' && value !== null && value !== undefined;
            })
            .forEach(([key, value]) => {
                params.set(key, value);
            });

        // if (!apartmentState.do) {
        //     params.delete('do');
        // }

        let baseUrl = '/noclegi-polska';
        const destination = apartmentState.kierunek;

        if (destination && typeof destination === 'string' && destination.trim() !== '') {
            baseUrl += `/${encodeURIComponent(destination)}`; 
        }

        const queryString = params.toString();
        const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        window.location.href = finalUrl;
    
        return false;
    };

    const resetFormHandler = () => {
        resetDestinations();
    }

    if (isLoading) {
        return <Loader />
    }

    if (errorMessage !== null) {
        return <Alert variant="danger" header={t('common.error_header')} message={errorMessage} />
    }

    return (
        <form onSubmit={submitHandler} className="trip-form trip-form--apartment">
            <div>
                <div className="trip-apartment-field-wrapper">
                    <CustomField
                        icon={destinationIcon}
                        title={t('form.destinations.field_label')}
                        placeholder={destinationPlaceholder}
                        modalId="destinations"
                        extraCssClass={`trip-apartment-field--destinations box-shadow ${
                            submitAttempted && isDestinationMissing
                                ? 'trip-apartment-field--invalid'
                                : ''
                        }`}
                        onReset={resetFormHandler}
                        isValid={true}
                    >
                        <DestinationsApartmentList
                            resetTrigger={resetFormHandler}
                        />
                    </CustomField>
                    {submitAttempted && isDestinationMissing && (
                        <div className="trip-apartment-field__error-message">
                            {t('form.destinations.error_message')}
                        </div>
                    )}
                </div>
                <CalendarField
                    icon={calendarIcon}
                    extraCssClass="trip-apartment-field--calendar box-shadow"
                >
                    <DateRangePicker
                        startLabel={t(`form.dates.start_field_label`)}
                        endLabel={t(`form.dates.end_field_label`)}
                        endPlaceholder={t(`form.dates.end_field_placeholder`)}
                    />
                </CalendarField>
                <CustomField
                    icon={participantIcon}
                    title={t('form.participants.field_label')}
                    placeholder={participantsPlaceholder}
                    modalId="participants"
                    extraCssClass="trip-apartment-field--participants box-shadow"
                    onReset={resetParticipantsHandler}
                    isValid={isValidParticipants}
                >
                    <Participants onValidityChange={setIsValidParticipants}/>
                </CustomField>
                <Button
                    label={t('common.btn_search')}
                    variant="primary"
                    type="submit"
                    extraCss="trip-btn--search box-shadow animation-bg-color"
                />
            </div>

            {Object.keys(accommodationTypes).length > 0 && (
                <fieldset className="trip-apartment-types">
                    {Object.entries(accommodationTypes).map(([value, label]) => (
                        <label
                            key={value}
                            className="filters__checkbox-wrapper"
                        >
                            {label}

                            <input
                                type="checkbox"
                                name="typ[]"
                                value={value}
                                checked={selectedTypes.includes(value)}
                                onChange={() => toggleAccommodationType(value)}
                            />

                            <span className="filters__checkbox-checkmark"></span>
                        </label>
                    ))}
                </fieldset>
            )}

            <input
                ref={honeypotRef}
                type="hidden"
                name="required"
                defaultValue=""
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                className="trip-required"
            />
        </form>
    )
};

ApartmentSearchForm.propTypes = {
    accommodationTypes: PropTypes.objectOf(PropTypes.string),
    customDestination: PropTypes.string,
    apartmentState: PropTypes.object,
};

const mapStateToProps = (state) => ({
    apartmentState: state.apartmentReducer,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setFormValuesByParams,
            setPopularCitiesList,
            setCitiesList,
            resetDestinations,
            resetParticipants
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(ApartmentSearchForm)
